<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="关键字" prop="keyword">
        <el-input
          v-model="queryParams.keyword"
          class="!w-240px"
          clearable
          placeholder="关键字/申请人/单号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态" prop="followUpStatus">
        <el-select
          v-model="queryParams.followUpStatus"
          class="!w-240px"
          clearable
          placeholder="-全部状态-"
        >
          <el-option :value="false" label="未跟进" />
          <el-option :value="true" label="已跟进" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-hasPermi="['crm:clue:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
        <el-button v-hasPermi="['crm:clue:import']" plain type="primary" @click="openImportForm">
          <Icon class="mr-5px" icon="ep:upload" />
          导入
        </el-button>
        <el-button
          v-hasPermi="['crm:clue:receive']"
          :disabled="selectedIds.length === 0"
          type="primary"
          @click="handleReceive(selectedIds)"
        >
          <Icon class="mr-5px" icon="ep:user-filled" />
          转为我的线索
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table
      v-loading="loading"
      :data="list"
      :show-overflow-tooltip="true"
      :stripe="true"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="55" />
      <el-table-column align="center" label="公司名称" fixed="left" prop="name" width="200">
        <template #default="scope">
          <el-link :underline="false" type="primary" @click="openDetail(scope.row.id)">
            {{ scope.row.name }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column align="center" label="联系人姓名" prop="contactName" width="110" />
      <el-table-column align="center" label="手机" prop="mobile" width="130" />
      <el-table-column align="center" label="微信号" prop="wechat" width="140" />
      <el-table-column align="center" label="QQ号" prop="qq" width="120" />
      <el-table-column align="center" label="需求" prop="requirement" width="200" />
      <el-table-column align="center" label="线索来源" prop="source" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_CUSTOMER_SOURCE" :value="scope.row.source" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="备注" prop="remark" width="200" />
      <el-table-column align="center" label="申请人" prop="creatorName" width="110" />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="来访时间"
        prop="visitTime"
        width="180px"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="转入线索池时间"
        prop="poolTime"
        width="180px"
      />
      <el-table-column
        label="操作"
        align="center"
        fixed="right"
        width="180"
        :show-overflow-tooltip="false"
      >
        <template #default="scope">
          <el-button
            link
            type="primary"
            v-hasPermi="['crm:clue:receive']"
            @click="handleReceive([scope.row.id])"
          >
            转为我的线索
          </el-button>
          <el-button link type="primary" @click="openDetail(scope.row.id)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 导入弹窗 -->
  <ClueImportForm ref="importFormRef" @success="getList" />
</template>

<script lang="ts" setup>
import { DICT_TYPE } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import download from '@/utils/download'
import * as ClueApi from '@/api/crm/clue'
import ClueImportForm from './ClueImportForm.vue'

defineOptions({ name: 'CrmCluePool' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref([]) // 列表的数据
const queryParams = ref<any>({
  pageNo: 1,
  pageSize: 10,
  pool: true, // 线索池：只查询负责人为空的线索
  keyword: undefined,
  followUpStatus: undefined
})
const queryFormRef = ref() // 搜索的表单
const exportLoading = ref(false) // 导出的加载中
const selectedIds = ref<number[]>([]) // 勾选的线索编号

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await ClueApi.getCluePage(queryParams.value)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.value.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.value = {
    pageNo: 1,
    pageSize: 10,
    pool: true,
    keyword: undefined,
    followUpStatus: undefined
  }
  handleQuery()
}

/** 勾选变化 */
const handleSelectionChange = (rows: ClueApi.ClueVO[]) => {
  selectedIds.value = rows.map((row) => row.id)
}

/** 打开线索详情 */
const { push } = useRouter()
const openDetail = (id: number) => {
  push({ name: 'CrmClueDetail', params: { id } })
}

/** 转为我的线索 */
const handleReceive = async (ids: number[]) => {
  if (!ids || ids.length === 0) {
    message.warning('请先勾选线索')
    return
  }
  try {
    await message.confirm(`确认将选中的 ${ids.length} 条线索转为我的线索吗？`)
    await ClueApi.receiveClue(ids)
    message.success(t('common.updateSuccess'))
    selectedIds.value = []
    await getList()
  } catch {}
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await ClueApi.exportClue(queryParams.value)
    download.excel(data, '线索池.xls')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 打开导入弹窗 */
const importFormRef = ref()
const openImportForm = () => {
  importFormRef.value.open()
}

/** 初始化 **/
onMounted(() => {
  getList()
})
</script>
